import React, { useState } from "react";
import {
  Modal,
  Box,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  DialogActions,
  Alert,
  Collapse,
  TextField,
} from "@mui/material";
import * as XLSX from "xlsx";
import StudyDropDown from "../../../common/components/StudyDropDown";
import { useCreateAgendaQuery } from "../../../api/useCreateAgenda";
import { useQueryClient } from "@tanstack/react-query";
import { parseISODate } from "../../../utils/date";
import { startOfWeek, format, addDays, differenceInDays } from "date-fns";
import { CatchingPokemon } from "@mui/icons-material";
import { useGetAgendasQuery } from "../../../api/useGetAgendas";

// 모달 스타일
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxHeight: "80vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};

const AgendaDialog = ({ open, closeModal }) => {
  const [scheduleData, setScheduleData] = useState({});
  const [originalData, setOriginalData] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [warning, setWarning] = useState("");
  const [showCopySection, setShowCopySection] = useState(false);
  const [copySourceStudy, setCopySourceStudy] = useState(null);
  const [showDateRange, setShowDateRange] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const queryClient = useQueryClient();
  const {
    mutate: createAgenda,
    isPending,
    error,
    isError,
  } = useCreateAgendaQuery();

  const {
    data: copyAgendas,
    isLoading: isCopyAgendasLoading,
    isError: isCopyAgendasError,
  } = useGetAgendasQuery({ studyId: copySourceStudy?._id });

  const handleClose = () => {
    setWarning("");
    setErrorMessage("");
    setOriginalData([]);
    setScheduleData({});
    setSelectedStudy(null);
    setShowCopySection(false);
    setCopySourceStudy(null);
    setShowDateRange(false);
    setStartDate("");
    setEndDate("");
    closeModal();
  };

  const handleCopyAgenda = () => {
    setShowCopySection(!showCopySection);
    if (showCopySection) {
      setCopySourceStudy(null);
    }
  };

  const handleLoadCopiedAgenda = () => {
    if (!copyAgendas || copyAgendas.length === 0) {
      setWarning("복사할 어젠다가 없습니다.");
      return;
    }

    setShowDateRange(true);
  };

  const handleApplyDateRange = () => {
    if (!startDate || !endDate) {
      setWarning("시작일과 종료일을 모두 입력해주세요.");
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      setWarning("시작일은 종료일보다 앞서야 합니다.");
      return;
    }

    // 원본 어젠다들을 날짜순으로 정렬
    const sortedAgendas = [...copyAgendas].sort(
      (a, b) => new Date(a.closeDate) - new Date(b.closeDate)
    );

    // 전체 기간 계산 (시작일과 종료일 포함)
    const totalDays = differenceInDays(end, start) + 1;
    const totalAgendas = sortedAgendas.length;

    // 날짜 개수와 어젠다 개수 비교
    if (totalDays < totalAgendas) {
      setWarning(
        `날짜 범위가 부족합니다. ${totalAgendas}개의 어젠다를 배치하려면 최소 ${totalAgendas}일이 필요합니다. (현재: ${totalDays}일)`
      );
      return;
    }

    if (totalDays > totalAgendas) {
      setWarning(
        `날짜 범위가 너무 많습니다. ${totalAgendas}개의 어젠다에 정확히 ${totalAgendas}일이 필요합니다. (현재: ${totalDays}일)`
      );
      return;
    }

    // 날짜와 어젠다 개수가 정확히 일치하는 경우에만 진행
    const formattedData = sortedAgendas.map((agenda, index) => {
      // 각 어젠다에 날짜를 순차적으로 할당
      const newDate = addDays(start, index);

      return {
        date: format(newDate, "yyyy.MM.dd"),
        lecture: agenda.lecture,
        assignment: agenda.assignment,
        level: agenda.level,
        submissionLinks: agenda.submissionLinks || [], // 객체 배열 그대로 유지
        note: agenda.note || "",
      };
    });

    setOriginalData(formattedData);
    organizeDataByWeek(formattedData);
    setShowCopySection(false);
    setShowDateRange(false);
    setStartDate("");
    setEndDate("");
    setWarning("");
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = (evt) => {
      const binaryStr = evt.target.result;
      const workbook = XLSX.read(binaryStr, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });
      setOriginalData(jsonData);
      organizeDataByWeek(jsonData);
    };

    reader.readAsBinaryString(file);
  };

  // Excel 파일로부터 읽어온 데이터를 주 단위로 구성하는 함수
  const organizeDataByWeek = (jsonData) => {
    const dataByWeek = {};
    const weekKeys = {}; // 주차 시작일을 주차 번호로 매핑
    let weekCounter = 1;

    jsonData.forEach((row) => {
      const date = row["date"]; // 날짜 컬럼이 'date'로 되어있다고 가정
      if (!date) return;

      const weekKey = getWeekNumber(new Date(date));

      // 새로운 주차인 경우 번호 할당
      if (!weekKeys[weekKey]) {
        weekKeys[weekKey] = weekCounter++;
      }

      const weekNumber = weekKeys[weekKey];

      if (!dataByWeek[weekNumber]) {
        dataByWeek[weekNumber] = [];
      }

      dataByWeek[weekNumber].push(row);
    });

    setScheduleData(dataByWeek);
  };

  // 날짜로부터 요일을 얻는 함수
  const getDayName = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { weekday: "short" }); // 'Mon', 'Tue', ...
  };

  // 날짜로부터 주 번호를 계산하는 함수
  const getWeekNumber = (date) => {
    // const startOfYear = new Date(date.getFullYear(), 0, 1);
    // const pastDaysOfYear = (date - startOfYear) / 86400000;
    // return Math.ceil((pastDaysOfYear + startOfYear.getDay() + 1) / 7);
    // const isoDate = parseISODate(date.split("T")[0]);
    const weekStart = startOfWeek(date, { weekStartsOn: 1 }); // 주 시작은 월요일로 설정
    const weekKey = format(weekStart, "yyyy-MM-dd");
    return weekKey;
  };

  const handleCreateAgenda = () => {
    if (!selectedStudy || originalData.length === 0) {
      return setWarning("스터디가 선택되지 않았거나 추가할 어젠다가 없습니다.");
    }

    createAgenda(
      { studyId: selectedStudy._id, data: originalData },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["agendas", selectedStudy._id]);
          closeModal();
        },
        onError: (error) => {
          setErrorMessage(
            error.response.data.message || "삭제에 실패했습니다."
          ); // 실패 시 에러 메시지 설정
        },
      }
    );
    setWarning("");
  };

  return (
    <div>
      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          <StudyDropDown
            selectedStudy={selectedStudy}
            handleStudyChange={(e) => setSelectedStudy(e.target.value)}
          />
          <Typography variant="h6" component="h2" sx={{ mt: 2, mb: 2 }}>
            스터디 어젠다
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Button variant="contained" component="label">
              Upload File
              <input
                type="file"
                hidden
                accept=".xlsx, .xls"
                onChange={handleFileUpload}
              />
            </Button>
            <Button variant="outlined" onClick={handleCopyAgenda}>
              {showCopySection ? "취소" : "어젠다 복사하기"}
            </Button>
          </Box>

          <Collapse in={showCopySection}>
            <Box
              sx={{
                p: 2,
                mb: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                복사할 스터디 선택
              </Typography>
              <StudyDropDown
                selectedStudy={copySourceStudy}
                handleStudyChange={(e) => setCopySourceStudy(e.target.value)}
              />
              {copySourceStudy && (
                <Box sx={{ mt: 2 }}>
                  {isCopyAgendasLoading ? (
                    <Typography>로딩 중...</Typography>
                  ) : isCopyAgendasError ? (
                    <Alert severity="error">
                      어젠다를 불러오는데 실패했습니다.
                    </Alert>
                  ) : copyAgendas && copyAgendas.length > 0 ? (
                    <>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {copyAgendas.length}개의 어젠다를 찾았습니다.
                      </Alert>
                      <Button
                        variant="contained"
                        onClick={handleLoadCopiedAgenda}
                      >
                        이 어젠다 불러오기
                      </Button>
                    </>
                  ) : (
                    <Alert severity="warning">
                      선택한 스터디에 어젠다가 없습니다.
                    </Alert>
                  )}
                </Box>
              )}
            </Box>
          </Collapse>

          <Collapse in={showDateRange}>
            <Box
              sx={{
                p: 2,
                mb: 2,
                border: "1px solid #1976d2",
                borderRadius: 1,
                bgcolor: "#f0f7ff",
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                새로운 날짜 범위 설정
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                총 {copyAgendas?.length || 0}개의 어젠다가 있습니다. 정확히{" "}
                {copyAgendas?.length || 0}일의 기간을 선택해주세요.
              </Alert>
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField
                  label="시작일"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  fullWidth
                />
                <TextField
                  label="종료일"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  fullWidth
                />
              </Box>
              {startDate && endDate && (
                <Typography
                  variant="body2"
                  sx={{ mb: 2, color: "text.secondary" }}
                >
                  선택된 기간:{" "}
                  {differenceInDays(new Date(endDate), new Date(startDate)) + 1}
                  일
                </Typography>
              )}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleApplyDateRange}
                  disabled={!startDate || !endDate}
                >
                  날짜 적용하기
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowDateRange(false);
                    setStartDate("");
                    setEndDate("");
                    setWarning("");
                  }}
                >
                  취소
                </Button>
              </Box>
            </Box>
          </Collapse>

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {warning && <Alert severity="warning">{warning}</Alert>}

          {Object.keys(scheduleData).map((weekNumber) => (
            <React.Fragment key={weekNumber}>
              <Typography
                variant="subtitle1"
                component="h3"
                sx={{ mt: 4, mb: 2 }}
              >
                Week {weekNumber}
              </Typography>
              <TableContainer component={Paper}>
                <Table
                  sx={{ minWidth: 650 }}
                  aria-label="weekly schedule table"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Day</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Lecture</TableCell>
                      <TableCell>Assignment</TableCell>
                      <TableCell>Level</TableCell>
                      <TableCell>Submission Link</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scheduleData[weekNumber].map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{getDayName(row["date"])}</TableCell>
                        <TableCell>{row["date"]}</TableCell>
                        <TableCell>{row["lecture"]}</TableCell>
                        <TableCell>{row["assignment"]}</TableCell>
                        <TableCell>{row["level"]}</TableCell>
                        <TableCell>
                          {Array.isArray(row["submissionLinks"]) &&
                          row["submissionLinks"].length > 0
                            ? row["submissionLinks"]
                                .map((link) =>
                                  typeof link === "object" ? link.title : link
                                )
                                .join(", ")
                            : row["submissionLinks"] || ""}
                        </TableCell>
                        <TableCell>{row["note"]}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </React.Fragment>
          ))}
          <DialogActions>
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={isPending}
            >
              취소
            </Button>
            <Button
              onClick={handleCreateAgenda}
              variant="contained"
              disabled={isPending}
            >
              추가
            </Button>
          </DialogActions>
        </Box>
      </Modal>
    </div>
  );
};

export default AgendaDialog;
